import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { purchaseOrdersAPI, suppliersAPI, companiesAPI, Supplier } from '../../services/api';
import {
    ArrowLeftIcon,
    PlusIcon,
    TrashIcon,
} from '@heroicons/react/24/outline';
import { Card, CardBody, Button, Spinner } from '../ui';

import NoteTemplateManager from './NoteTemplateManager';
import { noteTemplatesAPI } from '../../services/api';

interface Company {
    id: number;
    name: string;
    address?: string;
    city?: string;
    state?: string;
    country?: string;
    phone?: string;
    email?: string;
}

interface Product {
    id: number;
    name: string;
    sku: string;
    unit?: string;
}

interface LineItem {
    productId?: number;
    description: string;
    quantity: number;
    unitPrice: number;
    totalPrice: number;
}

interface PurchaseOrderFormProps {
    isEdit?: boolean;
}

const PurchaseOrderForm: React.FC<PurchaseOrderFormProps> = ({ isEdit }) => {
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [suppliers, setSuppliers] = useState<Supplier[]>([]);
    const [companies, setCompanies] = useState<Company[]>([]);
    const [products, setProducts] = useState<Product[]>([]);
    const [searchParams] = useSearchParams();
    const prefilledSupplierId = searchParams.get('supplierId') || '';
    const [showTemplateManager, setShowTemplateManager] = useState(false);

    const [form, setForm] = useState({
        supplierId: prefilledSupplierId,
        companyId: '',
        expectedDeliveryDate: '',
        currency: 'USD',
        notes: '',
    });
    const [items, setItems] = useState<LineItem[]>([{ productId: undefined, description: '', quantity: 1, unitPrice: 0, totalPrice: 0 }]);

    useEffect(() => {
        const loadData = async () => {
            try {
                const [suppRes, compRes] = await Promise.all([
                    suppliersAPI.getAll({ limit: 200 }),
                    companiesAPI.getAll(),
                ]);
                setSuppliers(suppRes.data || []);
                setCompanies(compRes || []);

                // Try to load products
                try {
                    const { data: prodData } = await (await import('../../services/api')).default.get('/products', { params: { limit: 500 } });
                    setProducts((prodData.data || prodData || []).map((p: any) => ({ id: p.id, name: p.name, sku: p.sku, unit: p.unit })));
                } catch { /* Products endpoint may not support listing */ }

                if (isEdit && id) {
                    setLoading(true);
                    const po = await purchaseOrdersAPI.getById(+id);
                    setForm({
                        supplierId: String(po.supplierId),
                        companyId: String(po.companyId),
                        expectedDeliveryDate: po.expectedDeliveryDate ? po.expectedDeliveryDate.slice(0, 10) : '',
                        currency: po.currency || 'USD',
                        notes: po.notes || '',
                    });
                    if (po.items?.length) {
                        setItems(po.items.map((it) => ({
                            productId: it.productId || undefined,
                            description: it.description,
                            quantity: it.quantity,
                            unitPrice: Number(it.unitPrice),
                            totalPrice: Number(it.totalPrice),
                        })));
                    }
                    setLoading(false);
                } else if (!isEdit) {
                    // Load default template for new PO
                    try {
                        const templates = await noteTemplatesAPI.getAll();
                        const defaultTemplate = templates.find(t => t.isDefault);
                        if (defaultTemplate) {
                            setForm(f => ({ ...f, notes: defaultTemplate.content }));
                        }
                    } catch { /* Ignore if templates fail to load */ }
                }
            } catch (err: any) {
                setError(err.response?.data?.message || 'Failed to load data');
                setLoading(false);
            }
        };
        loadData();
    }, [isEdit, id]);


    const addItem = () => setItems([...items, { productId: undefined, description: '', quantity: 1, unitPrice: 0, totalPrice: 0 }]);

    const removeItem = (index: number) => {
        if (items.length > 1) setItems(items.filter((_, i) => i !== index));
    };

    const updateItem = (index: number, field: keyof LineItem, value: any) => {
        const updated = [...items];
        (updated[index] as any)[field] = value;

        if (field === 'productId' && value) {
            const prod = products.find(p => p.id === +value);
            if (prod) updated[index].description = prod.name;
        }
        if (field === 'quantity' || field === 'unitPrice') {
            updated[index].totalPrice = Number((updated[index].quantity * updated[index].unitPrice).toFixed(2));
        }
        setItems(updated);
    };

    const totalAmount = items.reduce((sum, it) => sum + (it.totalPrice || 0), 0);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!form.supplierId || !form.companyId || items.length === 0) {
            setError('Supplier, company, and at least one item are required');
            return;
        }

        setSaving(true);
        setError('');
        try {
            const payload = {
                supplierId: +form.supplierId,
                companyId: +form.companyId,
                expectedDeliveryDate: form.expectedDeliveryDate || undefined,
                currency: form.currency,
                notes: form.notes || undefined,
                totalAmount: Number(totalAmount.toFixed(2)),
                items: items.map((it) => ({
                    productId: it.productId ? +it.productId : undefined,
                    description: it.description,
                    quantity: +it.quantity,
                    unitPrice: Number(it.unitPrice),
                    totalPrice: Number(it.totalPrice),
                })),
            };

            if (isEdit && id) {
                await purchaseOrdersAPI.update(+id, payload);
            } else {
                await purchaseOrdersAPI.create(payload);
            }
            navigate('/admin/purchase-orders');
        } catch (err: any) {
            setError(err.response?.data?.message || 'Failed to save purchase order');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return <div className="flex justify-center items-center h-64"><Spinner size="lg" /></div>;
    }

    return (
        <div className="px-4 sm:px-6 lg:px-8 py-8 max-w-5xl mx-auto">
            <div className="mb-6">
                <button onClick={() => navigate(prefilledSupplierId ? `/admin/suppliers/${prefilledSupplierId}` : '/admin/purchase-orders')} className="flex items-center text-sm text-neutral-600 hover:text-neutral-900 mb-4">
                    <ArrowLeftIcon className="h-4 w-4 mr-1" /> {prefilledSupplierId ? 'Back to Supplier' : 'Back to Purchase Orders'}
                </button>
                <h1 className="text-2xl font-bold text-neutral-900">{isEdit ? 'Edit Purchase Order' : 'New Purchase Order'}</h1>
            </div>

            {error && (
                <Card variant="bordered" className="mb-4 border-error-200 bg-error-50">
                    <CardBody className="p-4"><div className="text-sm text-error-700">{error}</div></CardBody>
                </Card>
            )}

            <form onSubmit={handleSubmit}>
                {/* Company & Supplier */}
                <Card variant="elevated" className="mb-6">
                    <CardBody className="p-6">
                        <h2 className="text-lg font-semibold text-neutral-900 mb-4">Order Details</h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-neutral-700 mb-1">Company *</label>
                                <select value={form.companyId} onChange={(e) => setForm({ ...form, companyId: e.target.value })} required
                                    className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500">
                                    <option value="">Select Company</option>
                                    {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-neutral-700 mb-1">Supplier *</label>
                                {prefilledSupplierId ? (
                                    <>
                                        <input type="text" value={suppliers.find(s => String(s.id) === prefilledSupplierId)?.name || `Supplier #${prefilledSupplierId}`} readOnly
                                            className="w-full px-3 py-2 border border-neutral-200 rounded-lg bg-neutral-100 text-neutral-700 cursor-not-allowed" />
                                        <p className="text-xs text-neutral-500 mt-1">Supplier is pre-selected from supplier page</p>
                                    </>
                                ) : (
                                    <select value={form.supplierId} onChange={(e) => setForm({ ...form, supplierId: e.target.value })} required
                                        className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500">
                                        <option value="">Select Supplier</option>
                                        {suppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                                    </select>
                                )}
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-neutral-700 mb-1">Expected Delivery Date</label>
                                <input type="date" value={form.expectedDeliveryDate} onChange={(e) => setForm({ ...form, expectedDeliveryDate: e.target.value })}
                                    className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500" />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-neutral-700 mb-1">Currency</label>
                                <select value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })}
                                    className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500">
                                    <option value="USD">USD</option>
                                    <option value="EUR">EUR</option>
                                    <option value="GBP">GBP</option>
                                </select>
                            </div>
                        </div>
                        <div className="mt-4">
                            <div className="flex justify-between items-center mb-1">
                                <label className="block text-sm font-medium text-neutral-700">Notes</label>
                                <button
                                    type="button"
                                    onClick={() => setShowTemplateManager(true)}
                                    className="text-xs text-primary-600 hover:text-primary-800 font-medium"
                                >
                                    Manage Templates
                                </button>
                            </div>
                            <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3}
                                className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500" />
                        </div>
                    </CardBody>
                </Card>




                {/* Line Items */}
                <Card variant="elevated" className="mb-6">
                    <CardBody className="p-6">
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="text-lg font-semibold text-neutral-900">Line Items</h2>
                            <Button type="button" variant="outline" size="sm" onClick={addItem} leftIcon={<PlusIcon className="h-4 w-4" />}>Add Item</Button>
                        </div>

                        <div className="space-y-4">
                            {items.map((item, index) => (
                                <div key={index} className="p-4 border border-neutral-200 rounded-lg bg-neutral-50">
                                    <div className="flex items-center justify-between mb-3">
                                        <span className="text-sm font-medium text-neutral-600">Item {index + 1}</span>
                                        {items.length > 1 && (
                                            <button type="button" onClick={() => removeItem(index)} className="text-error-500 hover:text-error-700">
                                                <TrashIcon className="h-4 w-4" />
                                            </button>
                                        )}
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
                                        {products.length > 0 && (
                                            <div className="md:col-span-2">
                                                <label className="block text-xs font-medium text-neutral-600 mb-1">Product</label>
                                                <select value={item.productId || ''} onChange={(e) => updateItem(index, 'productId', e.target.value || undefined)}
                                                    className="w-full px-2 py-1.5 text-sm border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500">
                                                    <option value="">None (manual)</option>
                                                    {products.map(p => <option key={p.id} value={p.id}>{p.sku} - {p.name}</option>)}
                                                </select>
                                            </div>
                                        )}
                                        <div className={products.length > 0 ? 'md:col-span-4' : 'md:col-span-6'}>
                                            <label className="block text-xs font-medium text-neutral-600 mb-1">Description *</label>
                                            <input type="text" value={item.description} onChange={(e) => updateItem(index, 'description', e.target.value)} required
                                                className="w-full px-2 py-1.5 text-sm border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500" />
                                        </div>
                                        <div className="md:col-span-2">
                                            <label className="block text-xs font-medium text-neutral-600 mb-1">Quantity *</label>
                                            <input type="number" min="1" value={item.quantity} onChange={(e) => updateItem(index, 'quantity', +e.target.value)} required
                                                className="w-full px-2 py-1.5 text-sm border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500" />
                                        </div>
                                        <div className="md:col-span-2">
                                            <label className="block text-xs font-medium text-neutral-600 mb-1">Unit Price *</label>
                                            <input type="number" min="0" step="0.01" value={item.unitPrice} onChange={(e) => updateItem(index, 'unitPrice', +e.target.value)} required
                                                className="w-full px-2 py-1.5 text-sm border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500" />
                                        </div>
                                        <div className="md:col-span-2">
                                            <label className="block text-xs font-medium text-neutral-600 mb-1">Total</label>
                                            <input type="text" value={item.totalPrice.toFixed(2)} readOnly
                                                className="w-full px-2 py-1.5 text-sm border border-neutral-200 rounded-lg bg-neutral-100 text-neutral-700" />
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Grand Total */}
                        <div className="mt-4 pt-4 border-t border-neutral-200 flex justify-end">
                            <div className="text-right">
                                <span className="text-sm text-neutral-600">Grand Total: </span>
                                <span className="text-xl font-bold text-neutral-900">{form.currency} {totalAmount.toFixed(2)}</span>
                            </div>
                        </div>
                    </CardBody>
                </Card>

                {/* Actions */}
                <div className="flex justify-end space-x-3 mt-6">
                    <Button type="button" variant="outline" onClick={() => navigate(prefilledSupplierId ? `/admin/suppliers/${prefilledSupplierId}` : '/admin/purchase-orders')}>Cancel</Button>
                    <Button type="submit" variant="primary" disabled={saving}>
                        {saving ? 'Saving...' : isEdit ? 'Update PO' : 'Create PO'}
                    </Button>
                </div>
            </form>

            {showTemplateManager && (
                <NoteTemplateManager
                    onSelect={(content) => {
                        setForm({ ...form, notes: content });
                        setShowTemplateManager(false);
                    }}
                    onClose={() => setShowTemplateManager(false)}
                />
            )}
        </div >
    );
};

export default PurchaseOrderForm;
